import { GroupBillingFields } from '@/components/group-billing-fields';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { MultiLanguageInput } from '@/components/ui/multi-language-input';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreateGroup } from '@/hooks/use-groups';
import { useProject } from '@/hooks/use-projects';
import { getLocalizedText } from '@/lib/utils';
import { createGroupSchema, type GroupFormData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function NewGroupPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading: projectLoading } = useProject(projectId!);
  const createGroup = useCreateGroup();

  const form = useForm<GroupFormData>({
    resolver: zodResolver(createGroupSchema) as Resolver<GroupFormData>,
    defaultValues: {
      projectId: projectId!,
      name: { nl: '', fr: '', en: '' },
      packagePrice: 0,
      singleRoomSupplement: undefined,
      invoiceDescription: undefined,
      depositDescription: undefined,
      invoiceRemark: undefined,
      invoiceAdjustments: [],
      isActive: true,
    },
  });

  const onSubmit = async (data: GroupFormData) => {
    await createGroup.mutateAsync(data);
    navigate(`/projecten/${projectId}`);
  };

  if (projectLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Project niet gevonden</p>
        <Button asChild variant="link">
          <Link to="/projecten">Terug naar projecten</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/projecten/${projectId}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Nieuwe Groep</h1>
          <p className="text-muted-foreground">voor {getLocalizedText(project.name)}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Groep Informatie</CardTitle>
          <CardDescription>Vul de gegevens in voor de nieuwe reisgroep</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Groepsnaam</FormLabel>
                    <FormControl>
                      <MultiLanguageInput placeholder="bijv. Wave 1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <GroupBillingFields control={form.control} />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={`/projecten/${projectId}`}>Annuleren</Link>
                </Button>
                <Button type="submit" disabled={createGroup.isPending}>
                  {createGroup.isPending ? 'Bezig met aanmaken...' : 'Groep Aanmaken'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
